import React, { useEffect, useState, useRef } from 'react';
import { Button, Modal, Table, InputNumber, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { FormInstance } from 'antd';

import {
  productionBomService,
  type ProductionBomListRow,
  type ProductionBomDetail,
  type ProductionBomItemDetail,
} from '@services/operator/productionBomService';

interface ApplyBomButtonProps {
  form: FormInstance;
  merchantId: string;
}

// Shape mirrors handleProductSelect in ProductItemCard:
//   product_id is a plain UUID string (not labelInValue) and the hidden
//   product_sku / product_name fields drive the FormFieldDisplay panel that
//   ProductItemCard renders once product_id is set. The product flags
//   (validity_days / manufacturing_date_required / batch_management_required)
//   drive the conditional batch / manufacturing-date / expiration prompts —
//   leaving them undefined silently disables those prompts, so we pass them
//   through when the BOM controller emits them.
//
// bom_linked / bom_snapshot are client-side-only fields used by WorkOrderForm
// to (a) live-recompute linked input quantities when output qty changes and
// (b) offer a reset-to-BOM action on rows whose link was broken by manual
// edit. Stripped before sending to BE in mapItemToPayload — strong_params
// would silent-drop them anyway, but explicit removal keeps the payload tidy.
interface BomSnapshot {
  product_id: string;
  product_sku: string;
  product_name: string;
  product_validity_days?: number | null;
  manufacturing_date_required?: boolean | null;
  batch_management_required?: boolean | null;
  inventory_type_id: { value: string; label: string } | null;
  // Only set on input rows — the unmultiplied per-set quantity from the BOM
  // definition. Output rows omit this (output qty == sets, no scaling factor).
  per_set_quantity?: number;
}

interface WorkOrderItemFormValue {
  role: 'input' | 'output';
  product_id: string;
  product_sku: string;
  product_name: string;
  product_validity_days?: number | null;
  manufacturing_date_required?: boolean | null;
  batch_management_required?: boolean | null;
  inventory_type_id: { value: string; label: string } | null;
  quantity: number;
  batch: string;
  expiration_date: null;
  note: string;
  custom_attributes: Record<string, unknown>;
  bom_linked?: boolean;
  bom_snapshot?: BomSnapshot;
}

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

function sanitizeSets(v: number | string | null | undefined): number {
  if (v == null) return 1;
  const n = typeof v === 'string' ? Number(v) : v;
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

function buildOutputItem(detail: ProductionBomDetail, sets: number): WorkOrderItemFormValue {
  const inventory_type_id = detail.output_inventory_type
    ? { value: detail.output_inventory_type.id, label: detail.output_inventory_type.name }
    : null;
  return {
    role: 'output',
    product_id: detail.output_product.id,
    product_sku: detail.output_product.sku,
    product_name: detail.output_product.name,
    product_validity_days: detail.output_product.validity_days ?? null,
    manufacturing_date_required: detail.output_product.manufacturing_date_required ?? null,
    batch_management_required: detail.output_product.batch_management_required ?? null,
    inventory_type_id,
    quantity: sets,
    batch: '',
    expiration_date: null,
    note: '',
    custom_attributes: {},
    bom_linked: true,
    bom_snapshot: {
      product_id: detail.output_product.id,
      product_sku: detail.output_product.sku,
      product_name: detail.output_product.name,
      product_validity_days: detail.output_product.validity_days ?? null,
      manufacturing_date_required: detail.output_product.manufacturing_date_required ?? null,
      batch_management_required: detail.output_product.batch_management_required ?? null,
      inventory_type_id,
    },
  };
}

function buildInputItem(item: ProductionBomItemDetail, sets: number): WorkOrderItemFormValue {
  const inventory_type_id = item.input_inventory_type
    ? { value: item.input_inventory_type.id, label: item.input_inventory_type.name }
    : null;
  return {
    role: 'input',
    product_id: item.input_product.id,
    product_sku: item.input_product.sku,
    product_name: item.input_product.name,
    product_validity_days: item.input_product.validity_days ?? null,
    manufacturing_date_required: item.input_product.manufacturing_date_required ?? null,
    batch_management_required: item.input_product.batch_management_required ?? null,
    inventory_type_id,
    quantity: item.quantity * sets,
    batch: '',
    expiration_date: null,
    note: '',
    custom_attributes: {},
    bom_linked: true,
    bom_snapshot: {
      product_id: item.input_product.id,
      product_sku: item.input_product.sku,
      product_name: item.input_product.name,
      product_validity_days: item.input_product.validity_days ?? null,
      manufacturing_date_required: item.input_product.manufacturing_date_required ?? null,
      batch_management_required: item.input_product.batch_management_required ?? null,
      inventory_type_id,
      per_set_quantity: item.quantity,
    },
  };
}

function formHasExistingItems(form: FormInstance): boolean {
  const values = form.getFieldsValue(true);
  const outputs = (values.output_items as unknown[]) || [];
  const inputs = (values.input_items as unknown[]) || [];
  return outputs.length > 0 || inputs.length > 0;
}

const ApplyBomButton: React.FC<ApplyBomButtonProps> = ({ form, merchantId }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [boms, setBoms] = useState<ProductionBomListRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedBomId, setSelectedBomId] = useState<string | null>(null);
  const [sets, setSets] = useState<number>(1);
  const [listLoading, setListLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);

  // Debounce search input → server-side search param
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput, search, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setListLoading(true);
      try {
        const params: Record<string, unknown> = {
          'production_boms.merchant_id': [merchantId],
          page,
          page_size: PAGE_SIZE,
          with_count: true,
        };
        if (search) params.search = search;
        const res = await productionBomService.getRecords(params);
        if (cancelled) return;
        if (res.success) {
          setBoms(res.data);
          const count = (res as { total_count?: number }).total_count;
          setTotalCount(typeof count === 'number' ? count : res.data.length);
        } else {
          console.error('[ApplyBomButton] getRecords returned error envelope:', res);
          message.error(res.message || t('shared.work_orders.apply_bom.load_error'));
        }
      } catch (e) {
        console.error('[ApplyBomButton] load BOMs failed:', e);
        if (!cancelled) {
          message.error(t('shared.work_orders.apply_bom.load_error'));
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, merchantId, search, page, t]);

  const columns = [
    { title: t('shared.work_orders.apply_bom.column_name'), dataIndex: 'name', key: 'name' },
    {
      title: t('shared.work_orders.apply_bom.column_output'),
      key: 'output',
      render: (_: unknown, row: ProductionBomListRow) => row.output_product.sku,
    },
    {
      title: t('shared.work_orders.apply_bom.column_items'),
      dataIndex: 'production_bom_items_count',
      key: 'count',
    },
  ];

  // Track in-flight apply so user can dismiss the modal mid-fetch without
  // having the late resolve overwrite the form.
  const applyTokenRef = useRef(0);

  const handleClose = () => {
    // Invalidate any in-flight performApply so a late resolve cannot reach
    // setFieldsValue after the user has explicitly cancelled.
    applyTokenRef.current++;
    setOpen(false);
    setSelectedBomId(null);
    setSearchInput('');
    setSearch('');
    setPage(1);
    setSets(1);
  };

  const performApply = async () => {
    if (!selectedBomId) return;
    const token = ++applyTokenRef.current;
    setApplyLoading(true);
    try {
      const res = await productionBomService.get(selectedBomId);
      if (token !== applyTokenRef.current) return;
      if (!res.success) {
        console.error('[ApplyBomButton] get returned error envelope:', res);
        message.error(res.message || t('shared.work_orders.apply_bom.apply_error'));
        return;
      }
      const detail = res.data;
      const safeSets = sanitizeSets(sets);
      const output_items = [buildOutputItem(detail, safeSets)];
      const input_items = detail.production_bom_items.map((it) => buildInputItem(it, safeSets));
      form.setFieldsValue({ output_items, input_items });
      handleClose();
    } catch (err) {
      console.error('[ApplyBomButton] apply BOM failed:', err);
      message.error(t('shared.work_orders.apply_bom.apply_error'));
    } finally {
      // Always reset loading. The token-check version was wrong because the
      // success path calls handleClose() which now bumps applyTokenRef as part
      // of the cancel-race fix, so `token === current` is false on success and
      // the spinner stayed stuck forever. The original "skip reset if
      // superseded" concern (newer performApply still in flight) is unreachable
      // anyway because confirmDisabled blocks a second click while loading.
      setApplyLoading(false);
    }
  };

  const handleApply = () => {
    if (!selectedBomId) return;
    if (sanitizeSets(sets) < 1) return;
    if (formHasExistingItems(form)) {
      setConfirmReplaceOpen(true);
      return;
    }
    void performApply();
  };

  const handleConfirmReplaceOk = async () => {
    setConfirmReplaceOpen(false);
    await performApply();
  };

  const handleConfirmReplaceCancel = () => {
    setConfirmReplaceOpen(false);
  };

  const safeSets = sanitizeSets(sets);
  const confirmDisabled = !selectedBomId || safeSets < 1 || applyLoading;

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        {t('shared.work_orders.apply_bom.button')}
      </Button>
      <Modal
        open={open}
        title={t('shared.work_orders.apply_bom.modal_title')}
        onCancel={handleClose}
        okText={t('shared.work_orders.apply_bom.confirm')}
        okButtonProps={{ disabled: confirmDisabled, loading: applyLoading }}
        onOk={handleApply}
        width={720}
        destroyOnHidden
      >
        <Input
          placeholder={t('shared.work_orders.apply_bom.search_placeholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ marginBottom: 12 }}
          allowClear
        />
        <Table
          rowKey="id"
          loading={listLoading}
          dataSource={boms}
          columns={columns}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: totalCount,
            showSizeChanger: false,
            onChange: (next) => setPage(next),
          }}
          locale={{ emptyText: t('shared.work_orders.apply_bom.empty') }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedBomId ? [selectedBomId] : [],
            onChange: (keys) => setSelectedBomId((keys[0] as string) ?? null),
          }}
          onRow={(row) => ({ onClick: () => setSelectedBomId(row.id) })}
        />
        <div style={{ marginTop: 12 }}>
          {t('shared.work_orders.apply_bom.sets_label')}{' '}
          <InputNumber
            min={1}
            value={sets}
            onChange={(v) => setSets(sanitizeSets(v as number | string | null))}
          />
        </div>
      </Modal>
      <Modal
        open={confirmReplaceOpen}
        title={t('shared.work_orders.apply_bom.replace_confirm_title')}
        okText={t('shared.work_orders.apply_bom.replace_confirm_ok')}
        okButtonProps={{ danger: true, loading: applyLoading }}
        onOk={handleConfirmReplaceOk}
        onCancel={handleConfirmReplaceCancel}
        destroyOnHidden
      >
        {t('shared.work_orders.apply_bom.replace_confirm')}
      </Modal>
    </>
  );
};

export default ApplyBomButton;
